import React, { useState, useRef, useEffect, useMemo } from 'react';
import axios from 'axios';
import GetCookie from '../utils/GetCookie';
import ErrorPopup from '../utils/popup/ErrorPopup';
import ProcessingPopup from '../utils/popup/ProcessingPopup';
import TrimContent from '../utils/TrimContent';
import { useNavigate } from 'react-router-dom';
import authNavigate from './AuthNav';
import { useTimezone } from '../utils/TimezoneContext';
import { clearFormatterCache, TIMEZONE_LIST, getTimezoneDisplayLabel, getBrowserTimezone } from '../utils/dateUtils';

// Mirrors the PlatformRole enum in the backend. Only IAM-console profiles
// carry platform_role — admin-console profiles simply won't have this field.
const PLATFORM_ROLES = [
  { value: 'ADMIN', label: 'Admin' },
  { value: 'GLOBAL_VIEWER', label: 'Global Viewer' },
  { value: 'COUNTRY_VIEWER', label: 'Country Viewer' },
  { value: 'SUPER_USER', label: 'Super User' },
  { value: 'STANDARD_USER', label: 'Standard User' }
];

const roleLabel = (value) => {
  const match = PLATFORM_ROLES.find(r => r.value === value);
  return match ? match.label : value;
};

const SEES_ALL_COUNTRIES = ['ADMIN', 'GLOBAL_VIEWER'];

const ProfilePopup = ({ profileData, consoleType, onClose, onProfileUpdate, isOpen }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false); // NEW
  const [isUpdatingTimezone, setIsUpdatingTimezone] = useState(false);
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [showUpdatedTag, setShowUpdatedTag] = useState(false);
  
  const fileInputRef = useRef(null);
  const accessToken = GetCookie('access_token');

  const navigate = useNavigate();
  const { timezone, setTimezone } = useTimezone();

  // Timezone UI state
  const [isTimezoneOpen, setIsTimezoneOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const timezoneDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (timezoneDropdownRef.current && !timezoneDropdownRef.current.contains(event.target)) {
        setIsTimezoneOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredTimezones = useMemo(() => {
    if (!searchQuery.trim()) return TIMEZONE_LIST;
    const q = searchQuery.toLowerCase();
    return TIMEZONE_LIST.filter(
      tz => tz.label.toLowerCase().includes(q) ||
            tz.value.toLowerCase().includes(q) ||
            tz.region.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const groupedTimezones = useMemo(() => {
    const groups = {};
    filteredTimezones.forEach(tz => {
      if (!groups[tz.region]) groups[tz.region] = [];
      groups[tz.region].push(tz);
    });
    return groups;
  }, [filteredTimezones]);

  const currentLabel = useMemo(() => {
    const found = TIMEZONE_LIST.find(tz => tz.value === timezone);
    if (found) return found.label;
    return timezone ? timezone.replace(/_/g, ' ').split('/').pop() : 'Select';
  }, [timezone]);

  const countryList = useMemo(() => {
    const countries = profileData?.countries || [];
    const headed = profileData?.headed_countries || [];
    const headedIds = new Set(headed.map(c => c.id));
    const merged = new Map();
    [...countries, ...headed].forEach(c => {
      if (c && !merged.has(c.id)) merged.set(c.id, c);
    });
    return Array.from(merged.values())
      .map(c => ({ ...c, isHead: headedIds.has(c.id) }))
      .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [profileData?.countries, profileData?.headed_countries]);

  const seesAllCountries = SEES_ALL_COUNTRIES.includes(profileData?.platform_role);
  const visibleCountries = seesAllCountries
    ? countryList.filter(c => c.isHead)
    : countryList;
  const hasHeadship = visibleCountries.some(c => c.isHead);

  const currentOffset = useMemo(() => {
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'shortOffset',
      });
      const parts = formatter.formatToParts(now);
      const offsetPart = parts.find(p => p.type === 'timeZoneName');
      return offsetPart ? offsetPart.value : '';
    } catch {
      return '';
    }
  }, [timezone]);

  const handleSelect = (tzValue) => {
    handleTimezoneChange(tzValue);
    setIsTimezoneOpen(false);
    setSearchQuery('');
  };

  const handleAutoDetect = () => {
    const browserTz = getBrowserTimezone();
    handleSelect(browserTz);
  };

  const handleTimezoneChange = async (newTimezone) => {
    setIsUpdatingTimezone(true);
    try {
      // 1. Save to backend
      await axios.post(
        '/pceapi/general/update-user-preferences',
        { timezone: newTimezone, console_type: consoleType },
        {
          headers: {
            Authorization: accessToken,
            'Content-Type': 'application/json'
          }
        }
      );

      // 2. Update ProfileCache in localStorage
      const cacheKey = `profile_cache_${consoleType}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const parsed = JSON.parse(cached);
        parsed.data.timezone = newTimezone;
        localStorage.setItem(cacheKey, JSON.stringify(parsed));
      }

      // 3. Update context (triggers re-render across all components)
      clearFormatterCache();
      setTimezone(newTimezone);

    } catch (error) {
      console.error('Error updating timezone:', error);
      setPopupErrorMessage('Failed to update timezone preference');
      setIsErrorPopupVisible(true);
    } finally {
      setIsUpdatingTimezone(false);
    }
  };

  // Handle camera button click
  const handleCameraClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setPopupErrorMessage('Invalid file type. Only JPG, JPEG, and PNG are allowed.');
      setIsErrorPopupVisible(true);
      return;
    }

    // Validate file size (5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setPopupErrorMessage('File size exceeds 5MB limit.');
      setIsErrorPopupVisible(true);
      return;
    }

    setSelectedFile(file);
    
    // Create preview URL
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewUrl(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle save photo
  const handleSavePhoto = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setShowUpdatedTag(false);

    try {
      const formData = new FormData();
      formData.append('profile_photo', selectedFile);
      formData.append('console_type', consoleType);

      const response = await axios.post(
        '/pceapi/general/update-profile-photo',
        formData,
        {
          headers: {
            Authorization: accessToken,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        const path = consoleType === 'admin-console' ? '/admin-login' : '/login';
        const navType = consoleType === 'admin-console' ? 'admin' : 'iam';
        authNavigate(path, navType, navigate);
        return;
      }

      if (data.status === 'failed') {
        setPopupErrorMessage(data.message || 'Failed to update profile photo');
        setIsErrorPopupVisible(true);
        setIsUploading(false);
        return;
      }

      // Success
      setShowUpdatedTag(true);
      setSelectedFile(null);
      setPreviewUrl(null);
      
      // Update parent component with new image URL
      if (onProfileUpdate && data.data?.profile_image_url) {
        onProfileUpdate(data.data.profile_image_url);
      }

    } catch (error) {
      console.error('Error uploading profile photo:', error);
      setPopupErrorMessage(error.message || 'Failed to upload profile photo');
      setIsErrorPopupVisible(true);
    } finally {
      setIsUploading(false);
    }
  };

  // NEW - Handle remove photo
  const handleRemovePhoto = async () => {
    if (!profileData?.profile_image_url) return;

    setIsRemoving(true);
    setShowUpdatedTag(false);

    try {
      const formData = new FormData();
      formData.append('console_type', consoleType);

      const response = await axios.post(
        '/pceapi/general/remove-profile-photo',
        formData,
        {
          headers: {
            Authorization: accessToken,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      const data = response.data;

      if (data.status === 'NotAuthorized') {
        const path = consoleType === 'admin-console' ? '/admin-login' : '/login';
        const navType = consoleType === 'admin-console' ? 'admin' : 'iam';
        authNavigate(path, navType, navigate);
        return;
      }

      if (data.status === 'failed') {
        setPopupErrorMessage(data.message || 'Failed to remove profile photo');
        setIsErrorPopupVisible(true);
        setIsRemoving(false);
        return;
      }

      // Success - notify parent to remove image
      if (onProfileUpdate) {
        onProfileUpdate(null); // Pass null to indicate photo removed
      }

    } catch (error) {
      console.error('Error removing profile photo:', error);
      setPopupErrorMessage(error.message || 'Failed to remove profile photo');
      setIsErrorPopupVisible(true);
    } finally {
      setIsRemoving(false);
    }
  };

  // Close error popup
  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
    setPopupErrorMessage('');
  };

  // Get current display image
  const getCurrentImage = () => {
    if (previewUrl) return previewUrl;
    if (profileData?.profile_image_url) return profileData.profile_image_url;
    return null;
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Dropdown container - follows FabricSelector pattern */}
      <div className="position-relative">
        <div 
          className="ev-message-card2 d-block ev-card-3 blur-background position-absolute z-1000 mt-2 end-0 profile-card-width"
        >
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-1">
            <p className="ev-message-card3 ev-btn-small mb-0">
              <i className="bi bi-person-circle me-2"></i>My Profile 
            </p>
            <button 
              className="ev-btn-light-fill"
              onClick={onClose}
            >
              <i className="bi bi-x-lg"></i>
            </button>
          </div>

          {/* Scrollable content */}
          <div className="">
            {/* Profile Image Section */}
             {/* Updated Tag */}
                {showUpdatedTag && (
                  <div className="text-end margin-top-1 me-2">
                    <span className="alert alert-success ev-radius-large ev-btn-xsmall ev-fs-10">
                      <i className="bi bi-check2-circle"></i> Updated
                    </span>
                  </div>

              )}
            <div className="text-center margin-bottom-3 margin-top-3">
              <div className="position-relative d-inline-block">
                {getCurrentImage() ? (
                  <img 
                    src={getCurrentImage()}  
                    className="profile-image-display"
                  />
                ) : (
                  <div className="profile-image-display">
                    <i className="bi bi-person-fill"></i>
                  </div>
                )}
                
                {/* Camera Button */}
                <button
                  className="ev-btn-lightblue-fill-round blur-background  ev-fs-15 ev-icon-btn-padding-reg profile-camera-btn"
                  onClick={handleCameraClick}
                  disabled={isUploading || isRemoving}
                >
                  <i className="bi bi-camera-fill"></i>
                </button>


              </div>

              {/* Hidden File Input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".jpg,.jpeg,.png"
                className="d-none"
                onChange={handleFileChange}
              />

              {/* Action Buttons */}
              <div className="margin-top-2">
                {/* Save Button (only show when file selected) */}
                {selectedFile && (
                  <>
                    <button
                      className="ev-btn-blue-outline-round  ev-bold-reg ev-btn-small me-2"
                      onClick={handleSavePhoto}
                      disabled={isUploading || isRemoving}
                    >
                      {isUploading ? (
                        <>
                          Saving
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-1"></i>
                          Save Photo
                        </>
                      )}
                    </button>
                    <button
                      className="ev-btn-light-fill ev-radius-large  ev-bold-reg ev-btn-small me-2"
                      onClick={() => {
                        setSelectedFile(null);
                        setPreviewUrl(null);
                      }}
                      disabled={isUploading || isRemoving}
                    >
                      Cancel
                    </button>
                  </>
                )}

                {/* Remove Picture Button (only show when photo exists and no file selected) */}
                {!selectedFile && profileData?.profile_image_url && (
                  <button
                    className="ev-btn-light-fill ev-radius-large ev-bold-reg ev-btn-small"
                    onClick={handleRemovePhoto}
                    disabled={isUploading || isRemoving}
                  >
                    {isRemoving ? (
                      <>
                        Removing
                      </>
                    ) : (
                      <>
                        <i className="bi bi-trash me-1"></i>
                        Remove Photo
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* User Details */}
            <div className="text-center">
              <p className="ev-reg-p-blue margin-bottom-2 ev-bold-med">{profileData?.name || 'Name not found'}</p>
              <div className="d-flex flex-wrap align-items-center justify-content-center gap-2 margin-bottom-3">
                <p className="ev-message-card3 ev-bold-reg ev-btn-small mb-0"><i className="bi bi-envelope"></i> {profileData?.email || 'Email not found'}</p>

                {/* Platform Role (IAM console only — admin profiles don't carry this field) */}
                {profileData?.platform_role && (
                  <p className="ev-message-card3 ev-bold-reg ev-btn-small mb-0">
                    <i className="bi bi-person-badge"></i> {roleLabel(profileData.platform_role)}
                  </p>
                )}

                {profileData?.platform_role && seesAllCountries && (
                  <p className="ev-message-card3 ev-bold-reg ev-btn-small mb-0">
                    <i className="bi bi-globe2"></i> All countries
                  </p>
                )}
              </div>

              {profileData?.platform_role && visibleCountries.length > 0 && (
                <div className="d-flex align-items-start justify-content-center gap-2 my-2 mt-1 me-1">
                  <span className="ev-bold-med ev-btn-small border-0 pe-1 text-nowrap">
                    {seesAllCountries ? (
                      <><i className="bi bi-star-fill"></i> Country Head of :</>
                    ) : (
                      <>
                        <i className="bi bi-geo-alt"></i> Countries :
                      </>
                    )}
                  </span>
                  <div
                    className="d-flex flex-wrap gap-1 text-start"
                    style={{ maxHeight: '66px', overflowY: 'auto', overflowX: 'hidden' }}
                  >
                    {visibleCountries.map(country => (
                      <span key={country.id} className="ev-message-card3 ev-btn-xsmall ev-bold-reg">
                        {!seesAllCountries && country.isHead && (
                          <i className="bi bi-star-fill me-1"></i>
                        )}
                        {country.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {profileData?.platform_role && !seesAllCountries && hasHeadship && (
                <p className="ev-message-card2 ev-btn-small mb-0 mt-1">
                  <i className="bi bi-info-circle me-1"></i>
                  Starred countries are the ones you head
                </p>
              )}

              {/* Timezone Selector */}
              <div className="d-flex align-items-center justify-content-center gap-2 my-2 mt-1 me-1">
                <span className="ev-bold-med ev-btn-small border-0 pe-1 text-nowrap" >
                  <i className="bi bi-clock"></i> Timezone :
                </span>
                
                <div className="position-relative d-inline-block text-start" ref={timezoneDropdownRef}>
                  {/* Trigger Button */}
                  <button
                    type="button"
                    className="ev-btn-lightblue-fill-round ev-btn-small ev-bold-reg text-nowrap"
                    onClick={() => !(isUploading || isRemoving) && setIsTimezoneOpen(!isTimezoneOpen)}
                    disabled={isUploading || isRemoving}
                  >
                    <i className="bi bi-globe2 me-1"></i>
                    {currentLabel} <span className="ev-card-1 ev-btn-xsmall ms-1">{currentOffset}</span>
                    <i className={`bi bi-chevron-${isTimezoneOpen ? 'up' : 'down'} ms-1`}></i>
                  </button>

                  {/* Dropdown */}
                  {isTimezoneOpen && (
                    <div
                      className="ev-message-card2 ev-card-3 blur-background position-absolute z-1000 mt-4 ev-padding-small"
                      style={{ width: '260px', right: 0, maxHeight: '300px', whiteSpace: 'normal' }}
                    >
                      {/* Search Input */}
                      <div className="position-relative mb-2">
                        <input
                          type="text"
                          className="form-control search-input ms-0"
                          placeholder="Search timezone"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          autoFocus
                        />
                        <button
                          type="button"
                          className="ev-btn-lightblue-fill-round search-bar-button"
                        >
                          <i className="bi bi-search"></i>
                        </button>
                      </div>

                      {/* Auto-detect option */}
                      <button
                        type="button"
                        className="ev-btn-light-fill ev-btn-small w-100 text-start mb-1 ev-radius-xsmall"
                        onClick={handleAutoDetect}
                      >
                        <i className="bi bi-geo-alt  me-1 "></i> Auto-detect
                      </button>

                      {/* Timezone List */}
                      <div className="scrollable-element" style={{ maxHeight: '180px', overflowY: 'auto' }}>
                        {Object.keys(groupedTimezones).length === 0 && (
                          <p className="ev-small-p-dark text-center mt-2">No timezones found</p>
                        )}
                        {Object.entries(groupedTimezones).map(([region, tzList]) => (
                          <div key={region} className="mb-2">
                            <label className="ev-message-card3 ev-btn-xsmall ev-bold-med mt-1 d-block ev-radius-xsmall ev-fs-13">
                              {region}
                            </label>
                            {tzList.map(tz => (
                              <button
                                key={tz.value}
                                type="button"
                                className={`ev-btn-small w-100 text-start ev-radius-small mb-0 border-0 my-1 ${
                                  tz.value === timezone 
                                    ? 'ev-btn-blue-fill-round' 
                                    : 'ev-btn-light-fill'
                                }`}
                                onClick={() => handleSelect(tz.value)}
                              >
                                {tz.label}
                                {tz.value === timezone && (
                                  <i className="bi bi-check2 ms-1"></i>
                                )}
                              </button>
                            ))}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>


            {/* Permission Sets (Only for IAM) */}
            {profileData?.permission_sets && profileData.permission_sets.length > 0 && (
              <>  <div className=" ev-bold-med ev-fs-12  mb-1">&nbsp; My Permissions</div>
              <div className="vh-10 scrollable-element ev-card-2 ev-padding-small">
              
                  {profileData.permission_sets.map((permSet, index) => (
                    <span 
                      key={index} 
                      className="ev-message-card2 ev-btn-xsmall ev-radius-large me-1 mb-1"
                    >
                       <TrimContent text= {permSet} maxLength={30} />
                     
                    </span>
                    
                  ))}
                                              
              </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Processing Popup */}
      {(isUploading || isRemoving || isUpdatingTimezone) && (
        <ProcessingPopup
          message={
            isUploading ? "Uploading profile photo"
            : isRemoving ? "Removing profile photo"
            : "Updating timezone"
          }
        />
      )}

      {/* Error Popup */}
      {isErrorPopupVisible && (
        <ErrorPopup 
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}
    </>
  );
};

export default ProfilePopup;